import React from 'react';
import { 
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  SquaresPlusIcon
} from '@heroicons/react/24/outline';

export default function AdminSettings() {
  return (
    <div className="px-8 py-8 max-w-[1000px] mx-auto flex flex-col gap-6 pb-20">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-2">
        <div>
          <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">Admin Settings</h1>
          <p className="text-[15px] text-slate-500 mt-1 font-medium">
            Manage your personal administrator profile, security credentials, and platform API integrations.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="bg-slate-100 border border-slate-200 text-slate-700 px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-slate-200 transition-colors shadow-sm">
            Discard Changes
          </button>
          <button className="bg-brand-600 text-white px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20">
            Save All Changes
          </button>
        </div>
      </div>

      {/* Admin Profile Card */}
      <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-blue-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <UserIcon className="w-6 h-6" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-[18px] font-extrabold text-slate-900 tracking-tight">Admin Profile</h2>
            <p className="text-[13px] text-slate-500 font-medium mt-0.5">Update your personal identification details</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          {/* Left: Avatar Side */}
          <div className="flex flex-col items-center gap-4 shrink-0">
            <div className="w-32 h-32 bg-[#1e293b] rounded-[24px] overflow-hidden relative shadow-inner flex flex-col items-center justify-center">
              <UserIcon className="w-14 h-14 text-[#334155] mb-1" strokeWidth={1.5} />
              <span className="text-[8px] font-extrabold text-slate-400 uppercase tracking-widest text-center leading-tight z-10">
                Superadmin<br/>Safe Work
              </span>
              {/* Optional styling overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/50"></div>
            </div>
            <button className="text-[13px] font-extrabold text-brand-600 hover:text-brand-800 transition-colors">
              Change Photo
            </button>
          </div>

          {/* Right: Form Side */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            <div>
              <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Full Name</label>
              <input 
                type="text" 
                defaultValue="Alexander Vance" 
                className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all" 
              />
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Email Address</label>
              <input 
                type="email" 
                defaultValue="alexander.vance@eliteai.io" 
                className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all" 
              />
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Admin Role</label>
              <input 
                type="text" 
                readOnly 
                defaultValue="Super Administrator" 
                className="w-full bg-slate-100/80 border border-slate-200 text-slate-500 text-[14px] font-bold px-4 py-3.5 rounded-xl cursor-not-allowed outline-none" 
              />
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Account ID</label>
              <input 
                type="text" 
                readOnly 
                defaultValue="UID-992-AXV" 
                className="w-full bg-slate-100/80 border border-slate-200 text-slate-500 text-[14px] font-bold px-4 py-3.5 rounded-xl cursor-not-allowed outline-none" 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Security Card */}
      <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-blue-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <LockClosedIcon className="w-6 h-6" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-[18px] font-extrabold text-slate-900 tracking-tight">Security</h2>
            <p className="text-[13px] text-slate-500 font-medium mt-0.5">Manage your account access credentials</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Current Password</label>
            <input 
              type="password" 
              defaultValue="••••••••" 
              className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all" 
            />
          </div>
          <div>
            <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">New Password</label>
            <input 
              type="password" 
              placeholder="Enter new password" 
              className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all placeholder:text-slate-400 placeholder:font-medium" 
            />
          </div>
          <div>
            <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">Confirm New Password</label>
            <input 
              type="password" 
              placeholder="Repeat new password" 
              className="w-full bg-white border border-slate-200 text-slate-900 text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all placeholder:text-slate-400 placeholder:font-medium" 
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button className="bg-white border border-slate-200 text-slate-700 px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-slate-50 transition-colors shadow-sm">
            Update Password
          </button>
        </div>
      </div>

      {/* Platform API Keys Card */}
      <div className="bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-blue-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <SquaresPlusIcon className="w-6 h-6" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-[18px] font-extrabold text-slate-900 tracking-tight">Platform API Keys</h2>
            <p className="text-[13px] text-slate-500 font-medium mt-0.5">Core integration secrets for the Elite AI backend</p>
          </div>
        </div>

        <div className="flex flex-col gap-6 mb-8">
          
          {/* Key 1: Serper.Dev */}
          <div className="bg-slate-50/80 border border-slate-100 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[12px] font-extrabold text-slate-700 uppercase tracking-widest">SERPER.DEV API KEY</span>
              <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-100/50 text-[10px] font-extrabold uppercase tracking-widest rounded-full flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> CONNECTED
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <input 
                  type="text" 
                  readOnly 
                  defaultValue="sk-serper-v2-8827494-001-vance" 
                  className="w-full bg-white border border-slate-200 text-slate-600 font-mono text-[14px] font-bold px-4 py-3.5 rounded-xl pr-12 focus:outline-none" 
                />
                <EyeIcon className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hover:text-slate-600 transition-colors" />
              </div>
              <button className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-3.5 rounded-xl text-[14px] font-bold transition-colors">
                Test
              </button>
            </div>
          </div>

          {/* Key 2: OpenRouter */}
          <div className="bg-slate-50/80 border border-slate-100 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[12px] font-extrabold text-slate-700 uppercase tracking-widest">OPENROUTER / LLM GATEWAY KEY</span>
              <span className="px-2.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-100/50 text-[10px] font-extrabold uppercase tracking-widest rounded-full flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div> ACTIVE
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <input 
                  type="text" 
                  readOnly 
                  defaultValue="sk-or-v1-992384-llm-gate-02" 
                  className="w-full bg-white border border-slate-200 text-slate-600 font-mono text-[14px] font-bold px-4 py-3.5 rounded-xl pr-12 focus:outline-none" 
                />
                <EyeIcon className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hover:text-slate-600 transition-colors" />
              </div>
              <button className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-3.5 rounded-xl text-[14px] font-bold transition-colors">
                Test
              </button>
            </div>
          </div>

        </div>

        <div className="flex justify-end">
          <button className="bg-brand-600 text-white px-8 py-3 rounded-xl text-[14px] font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20">
            Save Keys
          </button>
        </div>
      </div>

    </div>
  );
}